import type { TFunction } from 'i18next';
import { buildSeoMeta, type SeoMetaOptions } from '@link/share/utils';
import {
  createAppI18n,
  DEFAULT_LOCALE,
  SHINKANSEN_LOCALES,
  type Locale
} from './i18n';
import type { loader as rootLoader } from './root';

/**
 * Triplabo's SEO defaults, the SSR counterpart to `src/utils/seoConfig.ts`.
 *
 * The SPA's `triplaboSeo()` / `shinkansenSeo()` took a `TFunction` from the
 * component's `useTranslation()`. A route's `meta` export is not a component and
 * cannot use hooks, so it takes one from `metaT()` instead.
 */

/**
 * A `t` for a route's `meta`, from the root loader's data (`matches[0]`).
 *
 * `meta` also runs in the browser on client navigations, where only the
 * page's language is loaded — which is the one `meta` needs. The root loader
 * runs even when the page's own loader threw.
 */
export const metaT = ({
  locale,
  translations
}: ReturnType<typeof rootLoader>): TFunction =>
  createAppI18n(locale, translations).t;

/** Site name, appended to every title and sent as `og:site_name`. */
const SITE_NAME = 'Triplabo';

type SeoOverrides = Omit<
  SeoMetaOptions,
  'url' | 'locale' | 'siteName' | 'defaultLocale' | 'title' | 'description'
> & {
  title?: string;
  description?: string;
};

function build(
  t: TFunction,
  locale: Locale,
  url: string,
  fallbackTitleKey: string,
  fallbackDescriptionKey: string,
  overrides: SeoOverrides = {}
) {
  const { title, description, ...rest } = overrides;
  return buildSeoMeta({
    url,
    locale,
    siteName: SITE_NAME,
    defaultLocale: DEFAULT_LOCALE,
    title: title || t(fallbackTitleKey),
    description: description || t(fallbackDescriptionKey),
    ...rest
  });
}

/**
 * Default SEO for triplabo pages. A page with its own copy passes `title` and
 * `description`; anything omitted falls back to the site-wide strings, matching
 * the override model `triplaboSeo()` used in the SPA.
 */
export function triplaboSeo(
  t: TFunction,
  locale: Locale,
  url: string,
  overrides: SeoOverrides = {}
) {
  return build(
    t,
    locale,
    url,
    'triplabo.home.title',
    'triplabo.home.description',
    overrides
  );
}

/** Default SEO for Shinkansen pages, which are not available in Japanese. */
export function shinkansenSeo(
  t: TFunction,
  locale: Locale,
  url: string,
  overrides: SeoOverrides = {}
) {
  return build(
    t,
    locale,
    url,
    'shinkansen.views.product.title',
    'shinkansen.views.product.description',
    // Reuses the locale list i18n already owns, so the hreflang set cannot drift
    // from the languages the flow is actually translated into.
    { hreflangs: SHINKANSEN_LOCALES, ...overrides }
  );
}
